import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, PageHeader } from "@cofano/cck";
import { Box, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import { Info } from "@mui/icons-material";

import useTabs from "components/hooks/useTabs";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { Language } from "imports/Utils";

import { RelationTabs } from "view/relations/utils/RelationUtils";

import ProductGeneral from "./components/form/ProductGeneral";
import ProductDangerousGoods from "./components/form/ProductDangerousGoods";
import ProductNSTRGood from "./components/form/ProductNSTRGood";
import ProductIDTFGood from "./components/form/ProductIDTFGood";
import { ProductTabs } from "./utils/ProductUtils";
import { productSchema, ProductSchemaInput, ProductSchemaOutput } from "./utils/product.schema";
import { ProductFormFields } from "./utils/product.types";

import { GetDangerousGoodsMasterListDocument, GetProductFormFieldsDocument } from "__generated__/view/products/graphql/product.queries.generated";
import { CreateOrUpdateProductDocument } from "__generated__/view/products/graphql/product.mutations.generated";

const ProductCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();

	const [ loading, setLoading ] = useState(false);

	const [ getProduct, { data } ] = useLazyQuery(GetProductFormFieldsDocument);

	const relationId = location.state?.relationId as number | undefined;

	useEffect(() => {
		if (id) {
			getProduct({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	const product = data?.product ? data.product : null;

	return (
		<>
			<PageHeader>
				<PageHeader.Title>
					{Language("PRODUCT")}
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button
						onClick={() => navigate(relationId
							? `/view/relations/${relationId}/details`
							: (id ? `/view/products/${id}/details${window.location.search}` : "/view/products"), relationId ? { state: { tab: RelationTabs.Products } } : undefined)}
						variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit" form="product-form">
						{Language("SAVE")}
					</Button>

					<ExternalMasterdataWarningIcon entity={MasterdataEntity.Product} />
				</PageHeader.Actions>
			</PageHeader>

			{(product || !id) && (
				<ProductForm
					product={product}
					onSave={(product) => navigate(relationId
						? `/view/relations/${relationId}/details`
						: `/view/products/${product.id}/details${window.location.search}`, relationId ? { state: { tab: RelationTabs.Products } } : undefined)}
					setLoading={setLoading} />
			)}
		</>
	);
};

const ProductForm = ({ product, onSave, setLoading }: {
	product: ProductFormFields | null;
	onSave: (val: Pick<ProductFormFields, "id">) => void;
	setLoading: (val: boolean) => void;
}) => {
	const session = useStoreState(store => store.session);

	const defaultValues = useMemo<ProductSchemaInput>(() => ({
		id: product?.id || null,
		name: product?.name || "",
		code: product?.code || undefined,
		externalId: product?.externalId || "",
		abbreviation: product?.abbreviation || "",
		category: product?.category || null,
		previousCargoDisplayName: product?.previousCargoDisplayName || undefined,
		casNumber: product?.casNumber || undefined,
		nstrCode: product?.nstrCode || undefined,
		volumeCorrectionConversionMethod: product?.volumeCorrectionConversionMethod || undefined,
		densityCorrectionFactor: product?.densityCorrectionFactor || undefined,
		vaporPressure: product?.vaporPressure || undefined,
		vaporPressureMin: product?.vaporPressureMin || undefined,
		vaporPressureMax: product?.vaporPressureMax || undefined,
		vaporPressureTemperature: product?.vaporPressureTemperature || undefined,
		viscosity: product?.viscosity || undefined,
		viscosityMin: product?.viscosityMin || undefined,
		viscosityMax: product?.viscosityMax || undefined,
		flashPoint: product?.flashPoint || undefined,
		flashPointMin: product?.flashPointMin || undefined,
		flashPointMax: product?.flashPointMax || undefined,
		meltingPoint: product?.meltingPoint || undefined,
		meltingPointMin: product?.meltingPointMin || undefined,
		meltingPointMax: product?.meltingPointMax || undefined,
		boilingPoint: product?.boilingPoint || undefined,
		boilingPointMin: product?.boilingPointMin || undefined,
		boilingPointMax: product?.boilingPointMax || undefined,
		autoIgnitionTemperature: product?.autoIgnitionTemperature || undefined,
		autoIgnitionTemperatureMin: product?.autoIgnitionTemperatureMin || undefined,
		autoIgnitionTemperatureMax: product?.autoIgnitionTemperatureMax || undefined,
		selfAcceleratingDecompositionPolymerisationTemperature: product?.selfAcceleratingDecompositionPolymerisationTemperature || undefined,
		maximumContactTemperature: product?.maximumContactTemperature || undefined,
		lowerExplosiveLimit: product?.lowerExplosiveLimit || undefined,
		upperExplosiveLimit: product?.upperExplosiveLimit || undefined,
		acceptedVentFreeLevel: product?.acceptedVentFreeLevel || undefined,
		productInstructionsForVessel: product?.productInstructionsForVessel || undefined,
		heatingInstructionsForVessel: product?.heatingInstructionsForVessel || undefined,
		description: product?.description || undefined,
		remark: product?.remark || undefined,
		loadingType: product?.loadingType || "",
		densityKgM3: product?.densityKgM3 || undefined,
		densityKgM3Min: product?.densityKgM3Min || undefined,
		densityKgM3Max: product?.densityKgM3Max || undefined,
		properShippingNameNL: product?.properShippingNameNL || "",
		properShippingNameEN: product?.properShippingNameEN || "",
		properShippingNameDE: product?.properShippingNameDE || "",
		properShippingNameFR: product?.properShippingNameFR || "",
		density: product?.density || undefined,
		densityTemperature: product?.densityTemperature || undefined,
		productIDTFGoodId: product?.idtfGood?.id || undefined,
		productNSTRGoodId: product?.nstrGood?.id || undefined,
		productADNGoodId: product?.adnGood?.id || undefined,
		adnGoodInput: product?.adnAttributes
			? {
				tankerType: product.adnAttributes.tankerType || "",
				cargoTankDesign: product.adnAttributes.cargoTankDesign || "",
				cargoTankType: product.adnAttributes.cargoTankType || "",
				cargoTankEquipment: product.adnAttributes.cargoTankEquipment?.split(",") || [],
				samplingType: product.adnAttributes.samplingType || "",
				temperatureClass: product.adnAttributes.temperatureClass || "",
				requiredEquipment: product.adnAttributes.requiredEquipment?.split(",") || [],
				hazardCode: product.adnAttributes.hazardCode || "",
				openingPressureRelieveValve: product.adnAttributes.openingPressureRelieveValve || "",
				maxFillingDegreePercentage: product.adnAttributes.maxFillingDegreePercentage || "",
				explosionGroup: product.adnAttributes.explosionGroup || "",
			}
			: undefined,
		isDangerousGood: product?.isDangerousGood || false,
		isActive: product?.isActive || true,
		isValidated: product?.isValidated || false,
	}), [ product ]);

	const { control, handleSubmit, reset, watch, setValue } = useForm<ProductSchemaInput, unknown, ProductSchemaOutput>({
		resolver: zodResolver(productSchema),
		mode: "onSubmit",
		defaultValues: defaultValues,
	});

	const { selectedTab, selectTab } = useTabs<ProductTabs>({
		defaultSelected: ProductTabs.Nstr,
	});

	const [ createOrEditProduct ] = useMutation(CreateOrUpdateProductDocument);

	useEffect(() => {
		reset(defaultValues);
	}, [ defaultValues, reset ]);

	const { data } = useQuery(GetDangerousGoodsMasterListDocument);

	const save = (form: ProductSchemaOutput) => {
		setLoading(true);
		createOrEditProduct({
			variables: {
				input: {
					...form,
					code: form.code?.trim() || null,
					externalId: form.externalId?.trim() || null,
					abbreviation: form.abbreviation?.trim() || null,
					category: form.category ? { id: form.category.id } : null,
					description: form.description?.trim() || null,
					remark: form.remark?.trim() || null,
					loadingType: form.loadingType?.trim() || null,
					casNumber: form.casNumber?.trim() || null,
					volumeCorrectionConversionMethod: form.volumeCorrectionConversionMethod?.trim() || null,
					productInstructionsForVessel: form.productInstructionsForVessel?.trim() || null,
					heatingInstructionsForVessel: form.heatingInstructionsForVessel?.trim() || null,
					nstrCode: form.nstrCode || null,
					density: form.density?.trim() || null,
					densityTemperature: form.densityTemperature || null,
				},
			},
		}).then(resp => {
			onSave(resp.data!.createOrUpdateProduct);
		}).finally(() => setLoading(false));
	};

	return (
		<form id="product-form" onSubmit={handleSubmit(save)}>
			<ExternalMasterdataAlert entity={MasterdataEntity.Product} externalId={watch("externalId")} />

			<Grid container spacing={3}>
				<Grid size={{ lg: 5, md: 12, xs: 12 }}>
					<Card title={Language("DETAILS")} icon={<Info />}>
						<ProductGeneral
							control={control}
							watch={watch}
							setValue={setValue}
							session={session} />
					</Card>
				</Grid>

				<Grid size={{ lg: 7, md: 12, xs: 12 }}>
					<Card disablePadding>
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs value={selectedTab} onChange={(_, value) => selectTab(value)}>
								<Tab label={intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" })} value={ProductTabs.DangerousGoods} />)
								<Tab label={intl.formatMessage({ id: "PRODUCTS_NSTR" })} value={ProductTabs.Nstr} />
								<Tab label={intl.formatMessage({ id: "PRODUCTS_IDTF" })} value={ProductTabs.Idtf} />
								<Tab disabled label={intl.formatMessage({ id: "DOCUMENTS" })} value={ProductTabs.Documents} />
							</Tabs>
						</Box>

						<div style={{ padding: 16 }}>
							<div style={{ display: selectedTab === ProductTabs.DangerousGoods ? "inherit" : "none" }}>
								<ProductDangerousGoods
									adnGoods={(data?.adnProductList?.goods || [])}
									control={control}
									watch={watch}
									product={product}
									setValue={setValue} />
							</div>

							<div style={{ display: selectedTab === ProductTabs.Nstr ? "inherit" : "none" }}>
								<ProductNSTRGood
									product={product}
									nstrGoods={(data?.nstrProductList?.goods || [])}
									setValue={setValue} />
							</div>

							<div style={{ display: selectedTab === ProductTabs.Idtf ? "inherit" : "none" }}>
								<ProductIDTFGood
									product={product}
									idtfGoods={(data?.idtfProductList?.goods || [])}
									setValue={setValue} />
							</div>
						</div>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default ProductCreateEdit;
